<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, ref, watch } from 'vue';

import type { SensitiveCredentialsApi } from '~/services/sensitive-credentials-api/sensitive-credentials-api.types';

// eslint-disable-next-line import/order
import InstallationModal from '~/components/shared/InstallationModal.vue';
import { formatCredentialLoginForDisplay } from '~/utils/formatters';

import type { ResetCredentialForm } from '../schemas/resetCredentialSchema';

import usePartnerMigrationProjects from '../queries/usePartnerMigrationProjects';
import useResetPartnerCredential from '../queries/useResetPartnerCredential';
import { resetCredentialSchema } from '../schemas/resetCredentialSchema';

interface Props {
  credential: SensitiveCredentialsApi.Credential;
  modelValue: boolean;
  partnerId: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'close': [];
  'update:modelValue': [value: boolean];
}>();

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const currentStep = ref<1 | 2>(1);

const resetMutation = useResetPartnerCredential();
const isLoading = computed(() => resetMutation.isPending.value);

const {
  defineField,
  errors,
  handleSubmit,
  resetForm,
} = useForm<ResetCredentialForm>({
  validationSchema: toTypedSchema(resetCredentialSchema),
});

const [ticket, ticketProps] = defineField('ticket');
const [reason, reasonProps] = defineField('reason');

const currentPage = ref(1);
const PAGE_SIZE = 20;

const partnerIdRef = computed(() => props.partnerId);
const platformRef = computed(() => props.credential.manufacture_platform);

const credentialLoginLabel = computed(
  () => formatCredentialLoginForDisplay(props.credential.login),
);

const modalTitle = computed(
  () => `Resetar Credencial — ${props.credential.manufacture_platform} (${credentialLoginLabel.value})`,
);

const pageSizeRef = computed(() => PAGE_SIZE);

const {
  data: migrationData,
  isError: isMigrationError,
  isFetching: isMigrationFetching,
} = usePartnerMigrationProjects(partnerIdRef, platformRef, currentPage, pageSizeRef);

const projects = computed(() => migrationData.value?.data ?? []);
const meta = computed(() => migrationData.value?.meta);
const totalPages = computed(() => {
  if (!meta.value) return 1;
  return Math.ceil(meta.value.total / meta.value.pageSize);
});

const confirmedProjects = ref<Set<string>>(new Set());

const allProjectsConfirmed = computed(() => {
  const total = meta.value?.total ?? 0;
  return total === 0 || confirmedProjects.value.size >= total;
});

const allCurrentPageConfirmed = computed(
  () => projects.value.length > 0
    && projects.value.every((p) => confirmedProjects.value.has(p.project_id)),
);

function handleBack(): void {
  currentStep.value = 1;
}

function handleNext(): void {
  currentStep.value = 2;
}

function handlePageChange(page: number): void {
  currentPage.value = page;
}

function toggleConfirm(projectId: string): void {
  const next = new Set(confirmedProjects.value);
  if (next.has(projectId)) {
    next.delete(projectId);
  } else {
    next.add(projectId);
  }
  confirmedProjects.value = next;
}

function toggleCurrentPage(): void {
  const next = new Set(confirmedProjects.value);
  if (allCurrentPageConfirmed.value) {
    projects.value.forEach((p) => next.delete(p.project_id));
  } else {
    projects.value.forEach((p) => next.add(p.project_id));
  }
  confirmedProjects.value = next;
}

const onSubmit = handleSubmit(async (values) => {
  try {
    await resetMutation.mutateAsync({
      credentialId: props.credential.credential_id,
      partnerId: props.partnerId,
      payload: {
        reason: values.reason,
        ticket: values.ticket,
      },
    });

    isVisible.value = false;
    emit('close');
  } catch {
    //
  }
});

function handleCancel(): void {
  if (!isLoading.value) {
    isVisible.value = false;
    resetForm();
    emit('close');
  }
}

watch(isVisible, (newValue) => {
  if (!newValue) {
    resetForm();
    currentStep.value = 1;
    currentPage.value = 1;
    confirmedProjects.value = new Set();
  }
});
</script>

<template>
  <InstallationModal
    v-model="isVisible"
    :title="modalTitle"
    :loading="isLoading"
    :primary-button-disabled="currentStep === 1 && (!allProjectsConfirmed || isMigrationFetching)"
    :primary-button-text="currentStep === 1 ? 'Próximo →' : 'Confirmar Reset'"
    :primary-button-class="currentStep === 1 ? 'btn-primary' : 'btn-danger'"
    :secondary-button-text="currentStep === 1 ? 'Cancelar' : '← Voltar'"
    @primary-action="currentStep === 1 ? handleNext() : onSubmit()"
    @secondary-action="currentStep === 1 ? handleCancel() : handleBack()"
  >
    <template v-if="currentStep === 1">
      <p class="text-muted small mb-3">
        Passo 1 de 2 — Confirmar plantas migradas
      </p>

      <div class="alert alert-warning mb-3">
        <span>
          <strong>Atenção:</strong> Antes de resetar a credencial
          <strong>{{ credentialLoginLabel }}</strong> da plataforma
          <strong>{{ credential.manufacture_platform }}</strong>,
          confirme que todas as plantas listadas abaixo já foram migradas.
        </span>
      </div>

      <p class="text-muted small mb-3">
        A lista de plantas abaixo refere-se à plataforma
        <strong>{{ credential.manufacture_platform }}</strong>, não apenas ao login
        <strong>{{ credentialLoginLabel }}</strong>.
      </p>

      <div v-if="isMigrationFetching" class="text-center py-3">
        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
        Carregando plantas...
      </div>

      <div v-else-if="isMigrationError" class="alert alert-danger py-2 mb-0">
        Não foi possível carregar as plantas. Tente novamente.
      </div>

      <div v-else-if="projects.length === 0" class="text-muted small">
        Nenhuma planta encontrada para esta credencial.
      </div>

      <div v-else>
        <div class="table-responsive">
          <table class="table table-sm table-hover mb-2">
            <thead>
              <tr>
                <th>Nome da planta</th>
                <th>Inversores (SN)</th>
                <th class="text-center">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="allCurrentPageConfirmed"
                    aria-label="Marcar todas as plantas desta página"
                    :disabled="isLoading"
                    @change="toggleCurrentPage"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="project in projects" :key="project.project_id">
                <td>{{ project.plant_name }}</td>
                <td>
                  <div
                    v-for="inv in project.installed_inverters"
                    :key="inv.serial_number ?? 'sem-sn'"
                    class="small"
                  >
                    {{ inv.serial_number ?? '—' }}
                  </div>
                </td>
                <td class="text-center">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="confirmedProjects.has(project.project_id)"
                    :aria-label="`Confirmar migração da planta ${project.plant_name}`"
                    :disabled="isLoading"
                    @change="toggleConfirm(project.project_id)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <nav v-if="totalPages > 1" aria-label="Paginação de plantas">
          <ul class="pagination pagination-sm justify-content-center mb-2">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button
                type="button"
                class="page-link"
                :disabled="currentPage === 1 || isMigrationFetching || isLoading"
                @click="handlePageChange(currentPage - 1)"
              >
                «
              </button>
            </li>
            <li
              v-for="p in totalPages"
              :key="p"
              class="page-item"
              :class="{ active: p === currentPage }"
            >
              <button
                type="button"
                class="page-link"
                :disabled="isMigrationFetching || isLoading"
                @click="handlePageChange(p)"
              >
                {{ p }}
              </button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <button
                type="button"
                class="page-link"
                :disabled="currentPage === totalPages || isMigrationFetching || isLoading"
                @click="handlePageChange(currentPage + 1)"
              >
                »
              </button>
            </li>
          </ul>
        </nav>

        <div v-if="!allProjectsConfirmed" class="alert alert-info py-2 mt-2 mb-0 small">
          Confirme a migração de todas as plantas
          ({{ confirmedProjects.size }}/{{ meta?.total }}) para continuar.
        </div>
      </div>
    </template>

    <template v-else>
      <p class="text-muted small mb-3">
        Passo 2 de 2 — Preencher dados do reset
      </p>

      <div class="alert alert-danger mb-3">
        <span>
          <strong>Esta ação é irreversível.</strong> A credencial
          <strong>{{ credentialLoginLabel }}</strong> da plataforma
          <strong>{{ credential.manufacture_platform }}</strong>
          será desativada permanentemente.
        </span>
      </div>

      <div class="mb-3">
        <label class="form-label required" for="reset-ticket">
          Número do Ticket/Chamado
        </label>
        <input
          id="reset-ticket"
          v-model="ticket"
          v-bind="ticketProps"
          type="text"
          class="form-control"
          :class="{ 'is-invalid': errors.ticket }"
          placeholder="Ex: ZENDESK-12345"
          :disabled="isLoading"
        />
        <div v-if="errors.ticket" class="invalid-feedback">
          {{ errors.ticket }}
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label required" for="reset-reason">
          Motivo
        </label>
        <textarea
          id="reset-reason"
          v-model="reason"
          v-bind="reasonProps"
          class="form-control"
          :class="{ 'is-invalid': errors.reason }"
          rows="4"
          placeholder="Descreva o motivo do reset da credencial"
          :disabled="isLoading"
        />
        <div v-if="errors.reason" class="invalid-feedback">
          {{ errors.reason }}
        </div>
      </div>
    </template>
  </InstallationModal>
</template>

<style scoped lang="scss">
.form-label.required::after {
  content: ' *';
  color: var(--bs-danger);
}
</style>
